import { padStart } from "lodash";

export const getCurrentMonthName = () => new Date().toLocaleString("en-us", { month: "long" });

export const getNextMonthName = () => {
  const thisYear = new Date().getFullYear();
  const thisMonth = new Date().getMonth();

  return new Date(thisYear, thisMonth + 1, 1).toLocaleString("en-us", {
    month: "long",
  });
};

export const getOneMonthAgo = () => {
  const oneMonthAgo = new Date();
  return oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
};

export const getThreeDaysAgo = () => {
  const threeDaysAgo = new Date();
  return threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
};

export const getSimpleDate = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1; // Months are zero based
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

export const getSimpleDateUTC = (date: Date) => {
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1; // Months are zero based
  const year = date.getUTCFullYear();

  return `${month}/${day}/${year}`;
};

export const getUTCMonthAndDay = (date: Date) => {
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1; // Months are zero based

  return `${month}/${day}`;
};

export const getFriendlyDisplayDate = (date: Date) =>
  `${date.toLocaleString("en-us", {
    month: "long",
    timeZone: "UTC",
  })} ${date.getUTCDate()}`;

export const getUTCOffsetHours = () => {
  const now = new Date();
  const jan = new Date(now.getFullYear(), 0, 1);
  const isInDST = now.getTimezoneOffset() < jan.getTimezoneOffset();
  const offset = now.getTimezoneOffset() / 60;
  return isInDST ? offset + 1 : offset;
};

export const getFriendlyDisplayDateFullYear = (date: Date) =>
  `${date.toLocaleString("en-us", {
    month: "long",
    timeZone: "UTC",
  })} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;

export const toUrlSafeDate = (date: Date) =>
  [padStart(`${date.getUTCFullYear()}`, 2, "0"), padStart(`${date.getUTCMonth() + 1}`, 2, "0"), padStart(`${date.getUTCDate()}`, 2, "0")].join("-");

// This method will return the quarter the given date belongs to,
// i.e. "May, 2nd, 2019" will be "April, 1st, 2019".
export const getQuarterDate = (date: Date) => {
  // Get the quarter's month. UTC month is from 0 to 11, so if the month
  // is 8 => 8 - 8 % 3 => 8 - 2 => 6, and 6 is July.
  const quarterMonth = date.getUTCMonth() - (date.getUTCMonth() % 3);
  return new Date(date.getUTCFullYear(), quarterMonth, 1);
};

export const getNearbyQuarters = (date: Date, range: number) => {
  if (!date || range < 0) return [];

  const quarterDate = getQuarterDate(date);
  const quarterDates = [];

  const months = range * 3;
  for (let i = -months; i <= months; i += 3) {
    const quarter = new Date(quarterDate);
    quarter.setMonth(quarterDate.getMonth() + i);
    quarterDates.push(quarter);
  }

  return quarterDates;
};

export const getTargetEffectiveDateOptions = (
  useEndOfMonth: Boolean,
  groupEffectiveDate: Date,
  isRenewalGroup: Boolean,
  todayDate: Date = new Date(), // Default parameter value set to today's date
) => {
  const dateOptions = {};
  let optionsCount = isRenewalGroup || todayDate >= groupEffectiveDate ? 3 : 2;

  let startMonth = todayDate.getMonth();
  let startYear = todayDate.getFullYear();

  if (!isRenewalGroup && todayDate < groupEffectiveDate) {
    startMonth = groupEffectiveDate.getMonth();
    startYear = groupEffectiveDate.getFullYear();
  } else if (isRenewalGroup && todayDate < groupEffectiveDate) {
    // For renewal groups with today's date before the effective date, include the current month.
    optionsCount = 3;
  }

  for (let i = 0; i <= optionsCount; i++) {
    let month = startMonth + i;
    let year = startYear;

    // Adjust for year rollover.
    if (month > 11) {
      month %= 12;
      year++;
    }

    const day = useEndOfMonth ? new Date(year, month + 1, 0).getDate() : 1;

    const optionDate = new Date(year, month, day);
    const displayDate = optionDate.toLocaleString("en-us", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    dateOptions[displayDate] = getSimpleDate(optionDate);
  }

  // Adjust the options to remove any that are before the group's effective date for non-renewal groups.
  if (!isRenewalGroup) {
    Object.keys(dateOptions).forEach((key) => {
      const optionDate = new Date(dateOptions[key]);
      if (optionDate < groupEffectiveDate) {
        delete dateOptions[key];
      }
    });
  }

  return dateOptions;
};

// convert yyyy-mm-dd to mm/dd/yyyy
export const convertStringDateFormat = (date: string): string =>
  // $FlowFixMe -- Flow does not yet support method or property calls in optional chains.
  date
    ?.split("-")
    .map((_, i, str) => str[[1, 2, 0][i]])
    .join("/");

export const calculateAge = (bornAt: Date): number => Math.floor((new Date() - bornAt) / (1000 * 60 * 60 * 24 * 365));

export const getCancelDateOptions = (effectiveDate: Date, { medicalPlanCarrier, state }: { medicalPlanCarrier: string; state: string }) => {
  // the comments here have helped me visually group initialization logic. Worth considering moving the logic to helper methods
  // initialize currentDate
  const currentDate = new Date();
  currentDate.setUTCHours(0, 0, 0, 0);

  // initialize dateRangeMin and dateRangeMax
  const offset = getCancelDateOffset(medicalPlanCarrier, state);
  const dateRangeMin = new Date(currentDate);
  dateRangeMin.setUTCDate(dateRangeMin.getUTCDate() + offset);

  const rangeMaxOffset = offset > 0 ? offset + 90 : 90;
  const dateRangeMax = new Date(currentDate);
  dateRangeMax.setUTCDate(currentDate.getUTCDate() + rangeMaxOffset);

  // initialize dateOption
  const dateOption = new Date(dateRangeMin);
  dateOption.setUTCDate(effectiveDate.getUTCDate());

  // iterate through date range and push values into dateOptions
  const dateOptions = [];

  while (dateOption <= dateRangeMax) {
    if (dateOption > dateRangeMin) {
      const optionValue = getSimpleDateUTC(dateOption);
      const optionLabel = getFriendlyDisplayDateFullYear(dateOption);

      dateOptions.push([optionValue, optionLabel]);
    }
    dateOption.setUTCMonth(dateOption.getUTCMonth() + 1);
  }

  return dateOptions;
};

const getCancelDateOffset = (medicalPlanCarrier: string, state: string) => {
  // Business logic via jira https://simplyinsured.atlassian.net/browse/SI-11692
  let offset;
  if (medicalPlanCarrier === "Aetna") {
    offset = 30;
  } else if (state === "TX") {
    offset = 0;
  } else if (medicalPlanCarrier === "Kaiser" && state == "CO") {
    offset = -14;
  } else if (medicalPlanCarrier === "Kaiser" && state == "NW") {
    offset = 0;
  } else if (medicalPlanCarrier === "Kaiser" && state == "MD") {
    offset = 0;
  } else if (medicalPlanCarrier === "United Healthcare") {
    offset = -45;
  } else {
    offset = -30;
  }

  return offset;
};

export const getDateXDaysAwayFromToday = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
};
